// Site is on Pages (station-gold-portal.pages.dev). API is on the Worker; always use Workers URL when VITE_API_URL is unset so "Failed to fetch" is avoided.
const WORKERS_ORIGIN = 'https://station-gold-portal.mr-doug-sullivan.workers.dev';
const envOrigin = (import.meta.env.VITE_API_URL as string)?.replace(/\/$/, '') || '';
const API_ORIGIN = envOrigin || WORKERS_ORIGIN;
const API = `${API_ORIGIN}/api`;

/** Base URL for API (e.g. for document/resume links). */
export const getApiOrigin = (): string => API_ORIGIN || (typeof window !== 'undefined' ? window.location.origin : '');

/** Throw with a useful message so UI can show specific server errors (e.g. "Email required", "Unauthorized"). */
async function checkOk(r: Response): Promise<void> {
  if (r.ok) return;

  let message = `HTTP ${r.status}`;

  try {
    const contentType = r.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      const data = await r.json().catch(() => null as unknown);
      if (data && typeof data === 'object') {
        const anyData = data as { error?: unknown; detail?: unknown; message?: unknown };
        const parts: string[] = [];
        if (anyData.error && typeof anyData.error === 'string') parts.push(anyData.error);
        if (anyData.detail && typeof anyData.detail === 'string' && anyData.detail !== anyData.error) parts.push(anyData.detail);
        if (!parts.length && anyData.message && typeof anyData.message === 'string') parts.push(anyData.message);
        if (parts.length) {
          message = parts.join(' – ');
        }
      }
    } else {
      const text = await r.text();
      if (text) {
        message = text.slice(0, 200);
      }
    }
  } catch {
    // Fall through to generic HTTP status message.
  }

  throw new Error(message);
}

export async function getPartners(): Promise<{ partners: import('./types').Partner[] }> {
  const r = await fetch(`${API}/partners`);
  await checkOk(r);
  return r.json();
}

export async function getPartnersSummary(): Promise<{ partners: import('./types').PartnerSummary[] }> {
  const r = await fetch(`${API}/partners/summary`);
  await checkOk(r);
  return r.json();
}

export async function getPartner(id: string): Promise<import('./types').Partner> {
  const r = await fetch(`${API}/partners/${id}`);
  await checkOk(r);
  return r.json();
}

export async function createPartner(data: {
  name: string;
  email: string;
  tier: string;
  state_territory: string;
  status?: string;
  password?: string;
}): Promise<import('./types').Partner> {
  const r = await fetch(`${API}/partners`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function updatePartner(
  id: string,
  data: Partial<{ name: string; email: string; tier: string; state_territory: string; status: string; territory_status: string }>
): Promise<import('./types').Partner> {
  const r = await fetch(`${API}/partners/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function getReports(partnerId: string): Promise<{ reports: import('./types').ProductionReport[] }> {
  const r = await fetch(`${API}/partners/${partnerId}/reports`);
  await checkOk(r);
  return r.json();
}

export async function createReport(
  partnerId: string,
  data: {
    account_name: string;
    case_volume: number;
    collected_revenue: number;
    is_new_account: boolean;
    is_multi_unit: boolean;
    brand_messaging_compliance: boolean;
  }
): Promise<{ report: import('./types').ProductionReport; estimated_commission: { rate: number; amount: number } }> {
  const r = await fetch(`${API}/partners/${partnerId}/reports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function uploadReportDocument(
  partnerId: string,
  reportId: number,
  file: File
): Promise<{ document_url: string }> {
  const form = new FormData();
  form.append('file', file);
  const r = await fetch(`${API}/partners/${partnerId}/reports/${reportId}/document`, {
    method: 'PUT',
    body: form,
  });
  await checkOk(r);
  return r.json();
}

export async function getTierProgression(partnerId: string): Promise<import('./types').TierProgression> {
  const r = await fetch(`${API}/partners/${partnerId}/tier-progression`);
  await checkOk(r);
  return r.json();
}

export async function getTierAlerts(): Promise<{ threshold: number; alerts: import('./types').TierAlert[] }> {
  const r = await fetch(`${API}/tier-progression/alerts`);
  await checkOk(r);
  return r.json();
}

export async function estimateCommission(data: {
  tier: string;
  collected_revenue: number;
  is_new_account: boolean;
  commission_rate?: number;
}): Promise<{ rate: number; amount: number }> {
  const r = await fetch(`${API}/commission/estimate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function getLeaderboard(quarter?: string): Promise<import('./types').Leaderboard> {
  const url = quarter ? `${API}/leaderboard?quarter=${encodeURIComponent(quarter)}` : `${API}/leaderboard`;
  const r = await fetch(url);
  await checkOk(r);
  return r.json();
}

export async function getAdvancement(partnerId: string): Promise<import('./types').Advancement> {
  const r = await fetch(`${API}/partners/${partnerId}/advancement`);
  await checkOk(r);
  return r.json();
}

export async function getAtRisk(): Promise<import('./types').AtRiskResponse> {
  const r = await fetch(`${API}/territory/at-risk`);
  await checkOk(r);
  return r.json();
}

export async function getPartnerQuarterlyRevenue(partnerId: string): Promise<{ quarters: import('./types').QuarterlyRevenue[] }> {
  const r = await fetch(`${API}/partners/${partnerId}/quarterly-revenue`);
  await checkOk(r);
  return r.json();
}

export async function getProspects(partnerId: string): Promise<{ prospects: import('./types').Prospect[] }> {
  const r = await fetch(`${API}/partners/${partnerId}/prospects`);
  await checkOk(r);
  return r.json();
}

export async function createProspect(
  partnerId: string,
  data: { name: string; estimated_value: number; probability_score: number }
): Promise<import('./types').Prospect> {
  const r = await fetch(`${API}/partners/${partnerId}/prospects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function updateProspect(
  partnerId: string,
  prospectId: number,
  data: Partial<{ name: string; estimated_value: number; probability_score: number }>
): Promise<import('./types').Prospect> {
  const r = await fetch(`${API}/partners/${partnerId}/prospects/${prospectId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function deleteProspect(partnerId: string, prospectId: number): Promise<void> {
  const r = await fetch(`${API}/partners/${partnerId}/prospects/${prospectId}`, { method: 'DELETE' });
  await checkOk(r);
}

/** Returns the URL to view a document/resume by R2 key (served by Worker). */
export function getResumeUrl(key: string): string {
  const base = getApiOrigin();
  return `${base}/api/documents/${encodeURIComponent(key)}`;
}

export async function uploadResume(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const r = await fetch(`${API}/upload-resume`, { method: 'POST', body: form });
  await checkOk(r);
  return r.json();
}

export async function registerApplicant(data: {
  name: string;
  industry: string;
  gross_production: number;
  linkedin_url?: string;
  resume_r2_key?: string;
}): Promise<import('./types').Applicant & { id: number }> {
  const r = await fetch(`${API}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function getApplicants(adminSecret?: string): Promise<{ applicants: import('./types').Applicant[] }> {
  const headers: HeadersInit = {};
  if (adminSecret) headers['X-Admin-Secret'] = adminSecret;
  const r = await fetch(`${API}/admin/applicants`, { headers });
  await checkOk(r);
  return r.json();
}

export type LoginResult = { role: 'owner'; redirect: string } | { role: 'partner'; partnerId?: number; redirect: string };

export async function login(email: string, password: string): Promise<LoginResult> {
  const r = await fetch(`${API}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  await checkOk(r);
  return r.json();
}

export async function getPartnerDocuments(partnerId: string): Promise<{ documents: import('./types').PartnerDocument[] }> {
  const r = await fetch(`${API}/partners/${partnerId}/documents`);
  await checkOk(r);
  return r.json();
}

export async function uploadPartnerDocument(partnerId: string, file: File): Promise<import('./types').PartnerDocument> {
  const form = new FormData();
  form.append('file', file);
  const r = await fetch(`${API}/partners/${partnerId}/documents`, { method: 'POST', body: form });
  await checkOk(r);
  return r.json();
}

export async function updateDocumentStatus(
  partnerId: string,
  docId: number,
  status: import('./types').DocumentStatus
): Promise<import('./types').PartnerDocument> {
  const r = await fetch(`${API}/partners/${partnerId}/documents/${docId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  await checkOk(r);
  return r.json();
}

export type SystemUser = { type: 'admin'; id: number; name: null; email: string; role: string; commission_rate: null; created_at: string } | { type: 'partner'; id: number; name: string; email: string; role: string; commission_rate: number; created_at: string };

export async function getAdminUsers(adminSecret: string): Promise<{ users: SystemUser[] }> {
  const r = await fetch(`${API}/admin/users`, { headers: { 'X-Admin-Secret': adminSecret } });
  await checkOk(r);
  return r.json();
}

export async function createAdminUser(adminSecret: string, data: { type: 'admin' | 'partner'; email: string; password?: string; name?: string; role?: string; tier?: string; state_territory?: string; commission_rate?: number }): Promise<{ type: string; user: SystemUser }> {
  const r = await fetch(`${API}/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Secret': adminSecret },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function updateAdminUser(adminSecret: string, type: 'admin' | 'partner', id: number, data: { email?: string; name?: string; role?: string; password?: string; commission_rate?: number }): Promise<{ type: string; user: SystemUser }> {
  const r = await fetch(`${API}/admin/users/${type}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Secret': adminSecret },
    body: JSON.stringify(data),
  });
  await checkOk(r);
  return r.json();
}

export async function deleteAdminUser(adminSecret: string, type: 'admin' | 'partner', id: number): Promise<void> {
  const r = await fetch(`${API}/admin/users/${type}/${id}`, { method: 'DELETE', headers: { 'X-Admin-Secret': adminSecret } });
  await checkOk(r);
}
